import { ChamferOutline } from "@/components/chamfer";
import { type MouseEvent as ReactMouseEvent } from "react";

import { Column, Row, Text } from "@/components";
import { GoalPanelPeriodToggle } from "@/components/dashboard/dashboard-stats/main-stats/GoalPanelPeriodToggle";
import { TtfsGoalDelta } from "@/components/dashboard/dashboard-stats/main-stats/TtfsGoalDelta";

import {
  GOAL_PANEL_CONTAINER_CLASS,
  GOAL_PANEL_NARROW_LABEL_CLASS,
  GOAL_PANEL_NARROW_STACK_CLASS,
  SCREEN_HOURS_GREEN_MAX,
} from "@/lib/constants";
import { cn } from "@/lib/utils";
import { GoalPeriod, PerformanceTier } from "@/types";

// Medians come back in tenths of an hour, so subtracting the goal off one
// leaves float noise (26.9 - 12 = 14.899999999999999) to round away.
const TENTHS_PER_UNIT = 10;

// Selected, the panel wears its own grade: under 12h green, under 24h orange,
// past that red. Literal classes so Tailwind emits them.
const SELECTED_PANEL_CLASS: Record<PerformanceTier, string> = {
  [PerformanceTier.GREEN]:
    "border-lever-surface-accent-green bg-lever-surface-accent-green",
  [PerformanceTier.ORANGE]:
    "border-lever-surface-accent-copper bg-lever-surface-accent-copper",
  [PerformanceTier.RED]: "border-lever-icon-error bg-lever-icon-error",
};

export const TtfsGoalPanel = ({
  goalsPeriod,
  isIndividual,
  isSelected,
  medianHours,
  onGoalsPeriodChange,
  onOpenScreenBreakdown,
  tier,
}: {
  goalsPeriod: GoalPeriod;
  isIndividual: boolean;
  isSelected: boolean;
  medianHours: number | null;
  onGoalsPeriodChange: (period: GoalPeriod) => void;
  // Individual view only — the breakdown is per staffer.
  onOpenScreenBreakdown?: () => void;
  tier: PerformanceTier;
}) => {
  /* Helpers */
  const textClass = (() => {
    if (!isSelected) return "text-lever-text-heading";
    return "text-lever-text-on-accent";
  })();
  const hoursOffGoal =
    medianHours === null
      ? null
      : Math.round((medianHours - SCREEN_HOURS_GREEN_MAX) * TENTHS_PER_UNIT) /
        TENTHS_PER_UNIT;

  /* Handlers */
  // The panel behind it selects the metric, so the drill-down keeps the click.
  const handleMedianClick = (event: ReactMouseEvent) => {
    if (!onOpenScreenBreakdown) return;
    event.stopPropagation();
    onOpenScreenBreakdown();
  };

  /* Render */
  const render = () => (
    <Column
      className={cn(
        GOAL_PANEL_CONTAINER_CLASS,
        isSelected ? "lever-kpi-selected" : "lever-kpi-grid",
        "lever-chamfer-card relative h-full w-full justify-between rounded-lever-md border p-4 font-lever-suisse transition-colors",
        isSelected
          ? SELECTED_PANEL_CLASS[tier]
          : "border-lever-border-light bg-lever-surface-default",
      )}
    >
      <ChamferOutline key={String(isSelected)} isSelected={isSelected} />
      <Row className="items-start justify-between gap-2">
        <Text
          className={cn(
            "font-lever-accent text-[9px] font-normal uppercase tracking-wider",
            textClass,
          )}
        >
          {isIndividual ? "Time to first screen" : "Team TTFS Median"}
        </Text>
      </Row>
      {isSelected ? (
        <GoalPanelPeriodToggle
          onPeriodChange={onGoalsPeriodChange}
          period={goalsPeriod}
        />
      ) : null}
      <Row className={cn("items-end gap-2", GOAL_PANEL_NARROW_STACK_CLASS)}>
        <Column
          className={cn(
            "w-fit",
            onOpenScreenBreakdown && "cursor-pointer hover:opacity-80",
          )}
          onClick={handleMedianClick}
        >
          <Text
            className={cn(
              "lever-heading-h3 lever-kpi-accent whitespace-nowrap font-normal tabular-nums tracking-tight",
              textClass,
            )}
          >
            {medianHours === null ? "—" : `${medianHours.toString()}h`}
          </Text>
        </Column>
        <Text
          className={cn(
            GOAL_PANEL_NARROW_LABEL_CLASS,
            "pb-0.5 font-lever-accent text-[10px] font-normal opacity-80",
            textClass,
          )}
        >
          / {SCREEN_HOURS_GREEN_MAX.toString()}h
        </Text>
      </Row>
      <Column className="gap-1.5">
        <Row className="min-w-0 items-center gap-1">
          <TtfsGoalDelta hoursOffGoal={hoursOffGoal} textClass={textClass} />
        </Row>
        <Column className="h-1 w-full justify-center">
          <Column className="h-px w-full bg-lever-text-on-accent/60" />
        </Column>
      </Column>
    </Column>
  );

  return render();
};
